import type { AuthUser } from "../types/auth";

function normalizeRoles(values: string[] | undefined): Set<string> {
  return new Set(
    (values ?? [])
      .flatMap((value) => {
        const normalized = value.trim().toUpperCase().replace(/[\s-]+/g, "_");
        if (normalized.startsWith("VESSEL_") && normalized.length > "VESSEL_".length) {
          return [normalized, normalized.slice("VESSEL_".length)];
        }
        return [normalized];
      }),
  );
}

function normalizePermissions(values: string[] | undefined): Set<string> {
  return new Set(
    (values ?? [])
      .filter((value) => value.trim().length > 0)
      .map((value) => value.toUpperCase()),
  );
}

function hasAnyPermission(values: string[] | undefined, requiredValues: string[]): boolean {
  if (requiredValues.length === 0) {
    return false;
  }
  const granted = normalizePermissions(values);
  return requiredValues.some((value) => granted.has(value.toUpperCase()));
}

export function hasAnyRole(user: AuthUser | null | undefined, roles: string[]): boolean {
  if (!user) {
    return false;
  }
  const grantedRoles = normalizeRoles([user.role, ...(user.roles ?? [])]);
  return roles.some((role) => grantedRoles.has(role.toUpperCase()));
}

export function hasPermission(
  user: AuthUser | null | undefined,
  options: {
    roles?: string[];
    forms?: string[];
    processes?: string[];
  },
): boolean {
  if (!user) {
    return false;
  }

  const checks: boolean[] = [];

  if (options.roles?.length) {
    checks.push(hasAnyRole(user, options.roles));
  }
  if (options.forms?.length) {
    const grantedForms = normalizePermissions(user.form_ids ?? []);
    checks.push(options.forms.some((formId) => grantedForms.has(formId.toUpperCase())));
  }
  if (options.processes?.length) {
    const grantedProcesses = normalizePermissions(user.process_ids ?? []);
    checks.push(options.processes.some((processId) => grantedProcesses.has(processId.toUpperCase())));
  }

  if (checks.length === 0) {
    return false;
  }

  return checks.every(Boolean);
}

export function hasFormPermission(user: AuthUser | null | undefined, formIds: string[]): boolean {
  if (!user) {
    return false;
  }
  return hasAnyPermission(user.form_ids ?? [], formIds);
}

export function hasProcessPermission(user: AuthUser | null | undefined, processIds: string[]): boolean {
  if (!user) {
    return false;
  }
  return hasAnyPermission(user.process_ids ?? [], processIds);
}
